import { computed, ref, watch } from 'vue';

// COMPONENTS
import type { GInputCode } from '@flash-global66/g-input-code';

// COMPOSABLES
import { openAlert } from '@flash-global66/g-dialog-alert';
import { usePinFlow } from '../../composables/usePinFlow';

// TYPES
import type { PinEmits } from '../../types/pin.type';
import { PinType } from '../../types/pin.type';

// HELPERS
import {
  alertClosePinTransaction,
  alertForgotPinTransaction,
  onButtonAlertCloseTransaction,
} from '../../utils/alertPin';

export const usePinValidate = (emit: PinEmits, router) => {
  const pinFlow = usePinFlow();

  const pin = ref('');
  const isComplete = ref(true);
  const isSubmitting = ref(false);
  const inputCodeRef = ref<InstanceType<typeof GInputCode>>();

  const forgotPinText = computed(() => {
    return pinFlow.fromSignIn.value
      ? `¿Olvidaste tu PIN? Ingresa con código de seguridad`
      : '¿Olvidaste tu PIN?';
  });

  const FIELDS = 4;
  const routesClosePin = ['/transaction', '/exchange'];

  function onComplete(value: boolean) {
    isComplete.value = !value;
  }

  async function onChange(value: string) {
    pinFlow.setPinError('');
    pin.value = value;

    if (value.length === FIELDS && !isSubmitting.value) {
      try {
        isSubmitting.value = true;
        await pinFlow.onNext.value(value);
        if (!pinFlow.error.value) {
          emit('close');
          pinFlow.hidePin();
        }
      } finally {
        inputCodeRef.value?.clearValues();
        isSubmitting.value = false;
      }
    }
  }

  async function onClose() {
    const isClose = await checkCloseInRoute();
    if (isClose) {
      emit('close');
      pinFlow.hidePin();
    }
  }

  async function checkCloseInRoute(action = 'CLOSE'): Promise<boolean> {
    if (
      routesClosePin.includes(router?.currentRoute.value.path) &&
      pinFlow.show.value
    ) {
      let alertConfig;

      if (action === 'CLOSE') alertConfig = alertClosePinTransaction;
      if (action === 'RESET') alertConfig = alertForgotPinTransaction;

      const { isPrimary, isSecondary } = await openAlert(alertConfig);

      if (action === 'RESET') {
        if (isPrimary) return false;
        if (isSecondary) return true;

        return false;
      } else {
        onButtonAlertCloseTransaction({
          isPrimary,
          isSecondary,
          router,
        });
        return false;
      }
    }
    return true;
  }

  async function onReset() {
    const isClose = await checkCloseInRoute('RESET');
    if (isClose) {
      emit('close');

      pinFlow.hidePin();

      pinFlow.setFlowConfig({
        pinType: PinType.RESET,
      });

      pinFlow.showPin();
    }
  }

  async function handleForgotPin() {
    pinFlow.fromSignIn.value ? await pinFlow.onForgotPin.value() : onReset();
  }

  watch(
    () => router?.currentRoute.value.fullPath,
    () => {
      onClose();
    }
  );

  return {
    FIELDS,
    pinFlow,
    forgotPinText,
    inputCodeRef,
    onClose,
    onChange,
    onComplete,
    onReset,
    handleForgotPin,
  };
};
